import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { auth as appClientAuth, constants as appClientConstants, utils as appClientUtils } from '@edf-pkg/app-client';
import { store as appMainStore, useDialog } from '@edf-pkg/app-main';

import { DIALOG_IDS, UserSignActions } from '$app-web/constants';
import useErrorHandler from '$app-web/hooks/use-error-handler';
import { getCurrentTimezone } from '$app-web/utils';

import { useFetchAuthenticationTokens, useFetchMFAEnabled } from './';

const useSignIn = ({ onSuccess, onError, actionSource = UserSignActions.SignIn, silentError = false } = {}) => {
    const { handleError } = useErrorHandler();

    const dispatch = useDispatch();

    const formikActionsRef = useRef(null);
    const accountDeletionDialog = useDialog(DIALOG_IDS.ACCOUNT_DELETION_REVOKED);

    const updateAuthenticationStore = appClientAuth.useAuthenticationStore((store) => store.updateState);
    const username = appClientAuth.useAuthenticationStore((store) => store.username);
    const password = appClientAuth.useAuthenticationStore((store) => store.password);
    const isMFAEnabled = appClientAuth.useAuthenticationStore((store) => store.mfaEnabled);
    const setIsMFAEnabled = useCallback(
        (enabled) => {
            updateAuthenticationStore({ mfaEnabled: enabled });
        },
        [updateAuthenticationStore]
    );

    const {
        mutate: fetchAuthenticationTokens,
        isLoading: isFetchAuthenticationTokensLoading,
        isSuccess: isFetchAuthenticationTokensSuccess,
        isError: isFetchAuthenticationTokensError,
    } = useFetchAuthenticationTokens({
        onSuccess: (result) => {
            const { accessToken: _accessToken, refreshToken, expiresIn, revokedDeletion } = result;

            updateAuthenticationStore({
                accessToken: _accessToken,
                refreshToken,
                expiresIn,
                isAccountDeletionRevoked: revokedDeletion,
                // We need to reset the below values after a successful login
                mfaEnabled: false,
                username: null,
                password: null,
                otpToken: null,
            });

            dispatch(
                appMainStore.userDuck.duckActionCreators.setUserDataExternal({
                    userData: { apiKey: _accessToken },
                    actionSource,
                    shouldPersistUserData: true,
                    shouldLoadFromAPI: true,
                    shouldSetInitializationStatus: true,
                })
            );

            if (revokedDeletion) {
                accountDeletionDialog.open();
            }

            // We use the below flag to show a notification to users that an auto-logout occurred. We don't need to show them that notification anymore after they log in again.
            appClientUtils.auth.setAutoLogoutLocalStorageFlag(false);
        },
        onError: (error) => {
            onError?.(error);
            !silentError && handleError(error);
        },
    });

    const fetchTokens = useCallback(
        (values) => {
            const { username: userInputUsername, password: userInputPassword, token: otpToken } = values;
            const olsonTimezone = getCurrentTimezone();

            const headers = {
                ...(olsonTimezone && { 'X-Avc-Timezone': olsonTimezone }),
                ...(otpToken && { 'X-Avc-Otp': otpToken }),
            };

            const payload = {
                scope: appClientConstants.AUTHENTICATION_SCOPES.ALL,
                grant_type: appClientConstants.AUTHENTICATION_GRANT_TYPES.PASSWORD,
                client_id: appClientConstants.AUTHENTICATION_CLIENT_IDS.WEB,
                username: userInputUsername || username,
                password: userInputPassword || password,
            };

            fetchAuthenticationTokens({
                ...payload,
                extraAxiosConfig: {
                    headers,
                },
            });
        },
        [fetchAuthenticationTokens, password, username]
    );

    const {
        mutate: fetchMFAEnabled,
        isLoading: isFetchMFAEnabledLoading,
        isSuccess: isFetchMFAEnabledSuccess,
        isError: isFetchMFAEnabledError,
    } = useFetchMFAEnabled({
        onError: (error) => {
            onError?.(error);
            !silentError && handleError(error);
        },
    });

    const initiateSignIn = useCallback(
        (userInput, formikActions) => {
            const { username: userInputUsername, password: userInputPassword, token: otpToken } = userInput;

            formikActionsRef.current = formikActions;

            if (userInputUsername) {
                updateAuthenticationStore({ username: userInputUsername });
            }
            if (userInputPassword) {
                updateAuthenticationStore({ password: userInputPassword });
            }

            if (!otpToken) {
                fetchMFAEnabled(
                    { username: userInputUsername || username, password: userInputPassword || password, send: true },
                    {
                        onSuccess: (response) => {
                            const mfaEnabled = response.result.length > 0;
                            setIsMFAEnabled(mfaEnabled);
                            if (!mfaEnabled) {
                                fetchTokens({ username: userInputUsername, password: userInputPassword });
                            }

                            onSuccess?.({ mfaEnabled });
                        },
                    }
                );
            } else {
                fetchTokens({ token: otpToken });
            }
        },
        [fetchMFAEnabled, fetchTokens, onSuccess, password, setIsMFAEnabled, updateAuthenticationStore, username]
    );

    const isLoading = isFetchMFAEnabledLoading || isFetchAuthenticationTokensLoading;

    useEffect(() => {
        formikActionsRef.current?.setSubmitting?.(isLoading);
    }, [isLoading]);

    return {
        signIn: initiateSignIn,
        isLoading: isLoading,
        isSuccess: isFetchAuthenticationTokensSuccess && isFetchMFAEnabledSuccess,
        isError: isFetchAuthenticationTokensError || isFetchMFAEnabledError,
        isMFAEnabled,
        setIsMFAEnabled,
    };
};

export default useSignIn;
